<script setup lang="ts">
import { ref, reactive, computed, watch } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Tag from "primevue/tag";
import Chip from "primevue/chip";
import { useConfirm } from "src/composables/useConfirm";
import { useLayout } from "src/composables/useLayout";
import { statusSeverity, formatPrice, formatType, formatDate } from "src/utils/recipe-formatters";
import type { FetchRecipeResponseLite } from "src/api/models/fetchRecipeResponseLite";
import type { FetchRecipeResponse } from "src/api/models/fetchRecipeResponse";
import type { FetchActionResponse } from "src/api/models/fetchActionResponse";
import type { NestedActionRecipeResponse } from "src/api/models/nestedActionRecipeResponse";
import type { NestedArticleActionResponse } from "src/api/models/nestedArticleActionResponse";

const props = defineProps<{
  recipes: FetchRecipeResponseLite[];
  loading: boolean;
  disabled: boolean;
  page: number;
  totalPages: number;
  expandedRecipe: FetchRecipeResponse | null;
  expandedRecipeLoading: boolean;
  fetchActionDetail: (id: string) => Promise<FetchActionResponse>;
}>();

const emit = defineEmits<{
  pageChange: [page: number];
  expand: [id: string | null];
  editRecipe: [recipe: FetchRecipeResponseLite];
  deleteRecipe: [id: string];
  toggleStatus: [id: string, currentStatus: string];
  createAction: [recipeId: string];
  linkAction: [recipeId: string];
  editAction: [action: NestedActionRecipeResponse];
  removeAction: [recipeId: string, actionId: string];
  deleteAction: [id: string];
  toggleActionStatus: [id: string, currentStatus: string];
  linkArticleToRecipe: [recipeId: string];
  linkArticleToAction: [actionId: string];
  removeArticle: [abstractActionId: string, articleId: string];
}>();

const { confirm } = useConfirm();
const { tableSizeClass } = useLayout();
const expandedRows = ref<Record<string, boolean>>({});

watch(expandedRows, (newVal) => {
  const expandedIds = Object.keys(newVal).filter((k) => newVal[k]);
  emit("expand", expandedIds.length > 0 ? expandedIds[0] : null);
}, { deep: true });

const expandedActions = computed(() => props.expandedRecipe?.actions ?? []);
const expandedArticles = computed(() => props.expandedRecipe?.articles ?? []);

const expandedActionRows = ref<Record<string, boolean>>({});
const actionDetailsCache = reactive<Record<string, NestedArticleActionResponse[]>>({});
const actionDetailsLoading = reactive<Record<string, boolean>>({});

async function refetchActionDetail(actionId: string) {
  actionDetailsLoading[actionId] = true;
  try {
    const detail = await props.fetchActionDetail(actionId);
    actionDetailsCache[actionId] = detail.articles ?? [];
  } catch {
    // keep stale cache on error
  } finally {
    actionDetailsLoading[actionId] = false;
  }
}

defineExpose({ refetchActionDetail });

watch(expandedActionRows, async (newVal) => {
  for (const actionId of Object.keys(newVal)) {
    if (newVal[actionId] && !(actionId in actionDetailsCache)) {
      actionDetailsLoading[actionId] = true;
      try {
        const detail = await props.fetchActionDetail(actionId);
        actionDetailsCache[actionId] = detail.articles ?? [];
      } catch {
        actionDetailsCache[actionId] = [];
      } finally {
        actionDetailsLoading[actionId] = false;
      }
    }
  }
}, { deep: true });

watch(() => props.expandedRecipe, () => {
  expandedActionRows.value = {};
});

function onPage(event: { page: number }) {
  emit("pageChange", event.page + 1);
}

function onConfirmDelete(event: Event, id: string) {
  const { onAccept } = confirm(event);
  onAccept(() => emit("deleteRecipe", id));
}

function onConfirmRemoveAction(event: Event, recipeId: string, actionId: string) {
  const { onAccept } = confirm(event);
  onAccept(() => emit("removeAction", recipeId, actionId));
}

function onConfirmDeleteAction(event: Event, actionId: string) {
  const { onAccept } = confirm(event);
  onAccept(() => emit("deleteAction", actionId));
}
</script>

<template>
  <DataTable
    v-model:expandedRows="expandedRows"
    :value="recipes"
    :loading="loading"
    :class="`p-datatable-${tableSizeClass}`"
    class="border-round"
    data-key="id"
    striped-rows
    paginator
    :rows="25"
    :total-records="totalPages * 25"
    :first="(page - 1) * 25"
    lazy
    table-style="min-width: 60rem"
    @page="onPage"
  >
    <template #empty>
      <div class="text-center py-6 text-color-secondary">No recipes found</div>
    </template>

    <Column expander style="width: 3rem" />

    <Column field="name" header="Name" style="min-width: 220px">
      <template #body="{ data }">
        <span class="font-semibold select-text">{{ data.name ?? "-" }}</span>
      </template>
    </Column>

    <Column field="type" header="Type" style="min-width: 160px">
      <template #body="{ data }">
        <span class="select-text">{{ formatType(data.type) }}</span>
      </template>
    </Column>

    <Column field="status" header="Status" style="width: 100px">
      <template #body="{ data }">
        <Tag
          :value="data.status ?? 'unknown'"
          :severity="statusSeverity(data.status)"
          class="cursor-pointer"
          @click.stop="data.id && !disabled && $emit('toggleStatus', data.id, data.status ?? 'active')"
        />
      </template>
    </Column>

    <Column field="created_at" header="Created" style="min-width: 140px">
      <template #body="{ data }">
        <span class="select-text text-sm">{{ formatDate(data.created_at) }}</span>
      </template>
    </Column>

    <template #expansion="{ data: recipe }">
      <div class="py-3 px-4">
        <div v-if="expandedRecipeLoading" class="text-center py-6 text-color-secondary">
          <i class="pi pi-spin pi-spinner mr-2" />Loading...
        </div>

        <div v-else-if="expandedRecipe" class="flex flex-col gap-3">
          <div class="surface-card border-round border border-surface p-3">
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm font-bold uppercase text-color-secondary tracking-wide">Details</span>
              <div class="flex gap-1">
                <Button icon="pi pi-pencil" label="Edit recipe" size="small" text severity="info" :disabled="disabled" @click="$emit('editRecipe', recipe)" />
                <Button icon="pi pi-trash" size="small" text severity="danger" :disabled="disabled" @click="(e: Event) => recipe.id && onConfirmDelete(e, recipe.id)" />
              </div>
            </div>
            <table class="text-sm w-full" style="border-collapse: collapse">
              <tbody>
                <tr>
                  <td class="text-color-secondary pr-4 py-1" style="width: 80px">ID</td>
                  <td class="select-text font-mono text-xs py-1">{{ recipe.id }}</td>
                  <td class="text-color-secondary pr-4 py-1 pl-8" style="width: 80px">Created</td>
                  <td class="select-text py-1">{{ formatDate(recipe.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="surface-card border-round border border-surface p-3">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-bold uppercase text-color-secondary tracking-wide">
                Articles
                <span class="font-normal normal-case">({{ expandedArticles.length }})</span>
              </span>
              <Button icon="pi pi-plus" label="Add article" size="small" text severity="secondary" :disabled="disabled" @click="recipe.id && $emit('linkArticleToRecipe', recipe.id)" />
            </div>
            <div v-if="expandedArticles.length > 0" class="flex flex-wrap gap-2">
              <Chip
                v-for="article in expandedArticles"
                :key="article.id"
                :label="article.name ?? '-'"
                class="select-text"
                removable
                :disabled="disabled"
                @remove="article.id && recipe.id && $emit('removeArticle', recipe.id, article.id)"
              />
            </div>
            <div v-else class="text-sm text-color-secondary italic">No articles linked to this recipe</div>
          </div>

          <div class="surface-card border-round border border-surface p-3">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-bold uppercase text-color-secondary tracking-wide">
                Actions
                <span class="font-normal normal-case">({{ expandedActions.length }})</span>
              </span>
              <div class="flex gap-1">
                <Button icon="pi pi-link" label="Link existing" size="small" text severity="secondary" :disabled="disabled" @click="recipe.id && $emit('linkAction', recipe.id)" />
                <Button icon="pi pi-plus" label="New action" size="small" text severity="success" :disabled="disabled" @click="recipe.id && $emit('createAction', recipe.id)" />
              </div>
            </div>

            <DataTable
              v-if="expandedActions.length > 0"
              v-model:expandedRows="expandedActionRows"
              :value="expandedActions"
              data-key="id"
              size="small"
              striped-rows
              class="nested-table"
            >
              <Column expander style="width: 3rem" />
              <Column field="name" header="Name" style="min-width: 180px">
                <template #body="{ data }">
                  <span class="select-text">{{ data.name ?? "-" }}</span>
                </template>
              </Column>
              <Column field="type" header="Type" style="min-width: 130px">
                <template #body="{ data }">
                  <span class="select-text">{{ formatType(data.type) }}</span>
                </template>
              </Column>
              <Column field="price" header="Price" style="min-width: 90px">
                <template #body="{ data }">
                  <span class="select-text">{{ formatPrice(data.price) }}</span>
                </template>
              </Column>
              <Column field="status" header="Status" style="width: 90px">
                <template #body="{ data }">
                  <Tag
                    :value="data.status ?? 'unknown'"
                    :severity="statusSeverity(data.status)"
                    class="cursor-pointer"
                    @click="data.id && !disabled && $emit('toggleActionStatus', data.id, data.status ?? 'active')"
                  />
                </template>
              </Column>
              <Column header="" style="width: 140px" align-right>
                <template #body="{ data: action }">
                  <div class="flex gap-1 justify-end">
                    <Button icon="pi pi-box" text rounded size="small" severity="secondary" :disabled="disabled || !action.id" v-tooltip.top="'Add article'" @click="action.id && $emit('linkArticleToAction', action.id)" />
                    <Button icon="pi pi-pencil" text rounded size="small" severity="info" :disabled="disabled || !action.id" v-tooltip.top="'Edit'" @click="$emit('editAction', action)" />
                    <Button icon="pi pi-times" text rounded size="small" severity="warn" :disabled="disabled || !action.id" v-tooltip.top="'Unlink'" @click="(e: Event) => action.id && recipe.id && onConfirmRemoveAction(e, recipe.id, action.id)" />
                    <!-- <Button icon="pi pi-trash" text rounded size="small" severity="danger" :disabled="disabled || !action.id" v-tooltip.top="'Delete'" @click="(e: Event) => action.id && onConfirmDeleteAction(e, action.id)" /> -->
                  </div>
                </template>
              </Column>
              <template #expansion="{ data: action }">
                <div class="pl-6 py-2">
                  <div v-if="action.id && actionDetailsLoading[action.id]" class="text-sm text-color-secondary">
                    <i class="pi pi-spin pi-spinner mr-2" />Loading articles...
                  </div>
                  <div v-else-if="action.id && actionDetailsCache[action.id]?.length > 0" class="flex items-center gap-2 flex-wrap">
                    <span class="text-sm font-semibold text-color-secondary">Articles:</span>
                    <Chip
                      v-for="article in actionDetailsCache[action.id]"
                      :key="article.id"
                      :label="article.name ?? '-'"
                      class="select-text"
                      removable
                      :disabled="disabled"
                      @remove="article.id && action.id && $emit('removeArticle', action.id, article.id)"
                    />
                    <Button icon="pi pi-plus" size="small" text rounded severity="secondary" :disabled="disabled" v-tooltip.top="'Add article'" @click="action.id && $emit('linkArticleToAction', action.id)" />
                  </div>
                  <div v-else class="flex items-center gap-2">
                    <span class="text-sm text-color-secondary italic">No articles linked to this action</span>
                    <Button icon="pi pi-plus" label="Add article" size="small" text severity="secondary" :disabled="disabled" @click="action.id && $emit('linkArticleToAction', action.id)" />
                  </div>
                </div>
              </template>
            </DataTable>
            <div v-else class="text-sm text-color-secondary italic">No actions in this recipe</div>
          </div>
        </div>
      </div>
    </template>
  </DataTable>
</template>
